if (!customElements.get('lens-picker')) {
  customElements.define(
    'lens-picker',
    class LensPicker extends HTMLElement {
      connectedCallback() {
        this.inputs = Array.from(this.querySelectorAll('.lens-picker__input'));
        this.section = this.closest('.shopify-section') || this.closest('section');
        this.summary = this.querySelector('[data-lens-summary]');
        this.addDefaultText = this.dataset.addDefaultText || 'Add to cart';
        this.addCountText = this.dataset.addCountText || 'Add [count] items to cart';
        this.addRequiredText = this.dataset.addRequiredText || 'Please select at least 1 lens';
        this.selectedOne = this.dataset.selectedOne || "You've selected [count] lens";
        this.selectedOther = this.dataset.selectedOther || "You've selected [count] lenses";

        // Bundle total (light + selected lenses) shown in the main price block.
        this.sectionId = this.dataset.sectionId || '';
        this.lightPrice = parseInt(this.dataset.lightPrice, 10) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.priceContainer = document.getElementById(`price-${this.sectionId}`);
        this.basePriceHTML = this.priceContainer ? this.priceContainer.innerHTML : null;
        // Real availability of the current variant. We can't read this off the button,
        // because a lens-required light always renders disabled, so Dawn forces the
        // button to "Sold out" on every variant change regardless of stock.
        this.variantAvailable = this.dataset.lightAvailable !== 'false';

        this.inputs.forEach((input) => input.addEventListener('change', this.onChange.bind(this)));

        // Dawn re-renders / resets the Add button on a variant change, so
        // re-apply our state afterwards — and refresh the light's base price.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event && event.data && String(event.data.sectionId) === this.sectionId) {
              const variant = event.data.variant;
              this.variantAvailable = variant ? variant.available : false;
              if (variant && typeof variant.price === 'number') {
                this.lightPrice = variant.price;
              }
            }
            // Dawn has already re-rendered the price block (light-only) by now;
            // re-cache it as the base, then re-apply the total if lenses are picked.
            setTimeout(() => {
              if (this.priceContainer) this.basePriceHTML = this.priceContainer.innerHTML;
              this.updateAddButton();
              this.updateTotal();
            }, 0);
          });
        }

        this.onChange();
      }

      onChange() {
        this.updateAddButton();
        this.updateSummary();
        this.updateTotal();
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) this.variantUnsubscriber();
      }

      get required() {
        return this.dataset.required === 'true';
      }

      get addButton() {
        if (!this.section) return null;
        return this.section.querySelector('product-form [name="add"]');
      }

      /** Variant ids of the currently selected (and available) lenses. */
      getSelectedVariantIds() {
        return this.inputs
          .filter((input) => input.checked && !input.disabled)
          .map((input) => input.dataset.lensVariantId);
      }

      updateAddButton() {
        const button = this.addButton;
        if (!button) return;
        const label = button.querySelector('span');
        if (!label) return;

        // Genuinely sold-out / unavailable variant: leave Dawn's disabled state alone.
        // Otherwise (variant is in stock) re-apply the lens logic, overriding the
        // "Sold out" state Dawn forces on every variant change for lens-required lights.
        if (!this.variantAvailable) return;

        const count = this.getSelectedVariantIds().length;

        if (this.required && count === 0) {
          button.setAttribute('disabled', 'disabled');
          label.textContent = this.addRequiredText;
        } else if (count > 0) {
          button.removeAttribute('disabled');
          label.textContent = this.addCountText.replace('[count]', count + 1);
        } else {
          button.removeAttribute('disabled');
          label.textContent = this.addDefaultText;
        }
      }

      updateSummary() {
        if (!this.summary) return;
        const count = this.getSelectedVariantIds().length;
        if (count === 0) {
          this.summary.hidden = true;
          this.summary.textContent = '';
          return;
        }
        const template = count === 1 ? this.selectedOne : this.selectedOther;
        this.summary.textContent = template.replace('[count]', count);
        this.summary.hidden = false;
      }

      updateTotal() {
        if (!this.priceContainer || this.basePriceHTML == null) return;
        // Reset to the cached light-only markup so repeated updates don't accumulate.
        this.priceContainer.innerHTML = this.basePriceHTML;

        const selected = this.inputs.filter((input) => input.checked && !input.disabled);
        if (selected.length === 0) return; // light-only price stands

        const lensTotal = selected.reduce(
          (sum, input) => sum + (parseInt(input.dataset.lensPrice, 10) || 0),
          0
        );
        const total = (this.lightPrice || 0) + lensTotal;
        const formatted = this.formatMoney(total);

        // Overwrite whichever amount is visible (regular or, if on sale, the sale span).
        // Use innerHTML, not textContent: the shop's money format can contain markup
        // (this store's even has a stray </span>), and we want it parsed as HTML the
        // same way Liquid's `| money` renders it, rather than shown as literal text.
        const amountEls = [
          this.priceContainer.querySelector('.price__sale .price-item--sale'),
          this.priceContainer.querySelector('.price__regular > .price-item--regular'),
        ];
        amountEls.forEach((el) => {
          if (el) el.innerHTML = formatted;
        });
      }

      /** Minimal Shopify money formatter driven by the shop's money format. */
      formatMoney(cents) {
        const format = this.moneyFormat || '${{amount}}';
        const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        const withDelimiters = (number, precision, thousands, decimal) => {
          precision = isNaN(precision) ? 2 : precision;
          thousands = thousands == null ? ',' : thousands;
          decimal = decimal == null ? '.' : decimal;
          if (isNaN(number) || number == null) return '0';
          const parts = (number / 100.0).toFixed(precision).split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          const remainder = parts[1] ? decimal + parts[1] : '';
          return dollars + remainder;
        };

        const match = format.match(placeholder);
        const token = match ? match[1] : 'amount';
        let formatted;
        switch (token) {
          case 'amount_no_decimals':
            formatted = withDelimiters(value, 0);
            break;
          case 'amount_with_comma_separator':
            formatted = withDelimiters(value, 2, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            formatted = withDelimiters(value, 2, "'", '.');
            break;
          case 'amount_no_decimals_with_comma_separator':
            formatted = withDelimiters(value, 0, '.', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            formatted = withDelimiters(value, 0, ' ', '');
            break;
          case 'amount_with_space_separator':
            formatted = withDelimiters(value, 2, ' ', ',');
            break;
          default:
            formatted = withDelimiters(value, 2);
        }
        return format.replace(placeholder, formatted);
      }
    }
  );
}
